import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownAZ,
  Check,
  Clock3,
  Dumbbell,
  Minus,
  Play,
  Plus,
  RotateCcw,
  Shuffle,
} from "lucide-react";
import {
  DEFAULT_PRACTICE_PREFERENCES,
  PRACTICE_ROUND_MAX,
  PRACTICE_ROUND_MIN,
  STUDY_TARGET_MAX,
  STUDY_TARGET_MIN,
  practicePreferencesSchema,
  type PracticePreferences,
  type StudySource,
  type WordSense,
} from "@preptrack/contracts";
import { Button, EmptyState, LoadingLine, PageHeader } from "@/components/primitives";
import { CelebrationConfetti } from "@/components/celebration-confetti";
import { ShortcutHint } from "@/components/shortcut-hint";
import { WordSenses } from "@/components/word-senses";
import { WordPronunciation } from "@/components/word-pronunciation";
import { apiFetch } from "@/lib/api";
import { spellingMismatchIndexes, studyKeyboardCommand } from "@/lib/study-flow";
import type { StudySetupResponse } from "@/lib/study-types";

const STORAGE_KEY = "preptrack.free-practice.v1";

interface PracticeItem {
  wordId: string;
  headword: string;
  phoneticUk: string | null;
  phoneticUs: string | null;
  forms: unknown;
  notes: string | null;
  sourceSentence: string | null;
  senses: WordSense[];
  memberships: Array<{ code: string; label: string; kind: string }>;
}

interface StoredPracticeSettings {
  version: 1;
  value: PracticePreferences;
}

function loadSettings(): PracticePreferences {
  const requestedScope = new URLSearchParams(window.location.search).get("scope");
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as StoredPracticeSettings | null;
    if (stored?.version === 1) {
      const parsed = practicePreferencesSchema.safeParse(stored.value);
      if (parsed.success) return requestedScope === "favorites" ? { ...parsed.data, sources: ["favorites"] } : parsed.data;
    }
  } catch {
    // Invalid local preferences fall back to product defaults.
  }
  return requestedScope === "favorites"
    ? { ...DEFAULT_PRACTICE_PREFERENCES, sources: ["favorites"] }
    : DEFAULT_PRACTICE_PREFERENCES;
}

function saveSettings(value: PracticePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, value } satisfies StoredPracticeSettings));
}

function shuffle<T>(items: T[]) {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1));
    [next[index], next[swap]] = [next[swap]!, next[index]!];
  }
  return next;
}

export function PracticePage() {
  const setupQuery = useQuery({ queryKey: ["study", "setup"], queryFn: () => apiFetch<StudySetupResponse>("/study/setup") });
  const [settings, setSettings] = useState<PracticePreferences>(loadSettings);
  const [items, setItems] = useState<PracticeItem[]>([]);
  const [roundItems, setRoundItems] = useState<PracticeItem[]>([]);
  const [round, setRound] = useState(1);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<"recall" | "spelling" | "feedback" | "completed">("recall");
  const [revealed, setRevealed] = useState(false);
  const [answer, setAnswer] = useState("");
  const [correct, setCorrect] = useState(true);
  const [attemptCount, setAttemptCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const item = roundItems[index];

  const updateSettings = (patch: Partial<PracticePreferences>) => {
    setSettings((current) => ({ ...current, ...patch }));
  };
  const toggleSource = (source: StudySource, selected: boolean) => {
    updateSettings({ sources: selected ? [...new Set([...settings.sources, source])] : settings.sources.filter((value) => value !== source) });
  };
  const start = async () => {
    setStarting(true);
    setError("");
    try {
      const parsed = practicePreferencesSchema.parse(settings);
      saveSettings(parsed);
      const response = await apiFetch<{ items: PracticeItem[] }>("/practice/sets", { method: "POST", body: JSON.stringify(parsed) });
      if (!response.items.length) {
        setError("当前范围没有可练单词，请调整后重试。");
        return;
      }
      setItems(response.items);
      setRoundItems(response.items);
      setRound(1);
      setIndex(0);
      setPhase(response.items.length ? "recall" : "completed");
      setRevealed(false);
      setAnswer("");
      setAttemptCount(0);
      setErrorCount(0);
    } catch {
      setError("无法创建自由练习，请检查所选范围后重试。");
    } finally {
      setStarting(false);
    }
  };
  const finishRecall = (remembered: boolean) => {
    setAttemptCount((value) => value + 1);
    if (!remembered) {
      setCorrect(false);
      setErrorCount((value) => value + 1);
      setPhase("feedback");
    } else if (settings.spellingEnabled) {
      setPhase("spelling");
    } else {
      setCorrect(true);
      setPhase("feedback");
    }
  };
  const finishSpelling = () => {
    if (!item) return;
    const matches = answer.normalize("NFC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en") === item.headword.normalize("NFC").trim().replace(/\s+/g, " ").toLocaleLowerCase("en");
    setAttemptCount((value) => value + 1);
    setCorrect(matches);
    if (!matches) setErrorCount((value) => value + 1);
    setPhase("feedback");
  };
  const next = () => {
    if (index + 1 < roundItems.length) {
      setIndex((value) => value + 1);
      setPhase("recall");
      setRevealed(false);
      setAnswer("");
      return;
    }
    if (round < settings.roundCount) {
      setRound((value) => value + 1);
      setRoundItems(settings.order === "random" ? shuffle(items) : items);
      setIndex(0);
      setPhase("recall");
      setRevealed(false);
      setAnswer("");
      return;
    }
    setPhase("completed");
  };
  const reset = () => {
    setItems([]);
    setRoundItems([]);
    setPhase("recall");
    setError("");
  };

  useEffect(() => {
    if (!items.length && phase !== "completed") return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && phase !== "completed") {
        event.preventDefault();
        reset();
        return;
      }
      if (phase === "spelling") return;
      const stage = phase === "recall" ? "recall" : phase === "feedback" ? "feedback" : "completion";
      const command = studyKeyboardCommand(event, stage, revealed);
      if (!command) return;
      event.preventDefault();
      if (command === "reveal") setRevealed(true);
      if (command === "forgot") finishRecall(false);
      if (command === "remembered") finishRecall(true);
      if (command === "advance") phase === "feedback" ? next() : reset();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (setupQuery.isLoading) return <div className="page"><LoadingLine /></div>;
  if (!setupQuery.data) return <div className="page"><EmptyState title="自由练习暂时无法加载" description="请刷新页面后重试。" /></div>;

  if (!items.length && phase !== "completed") {
    const selectedTotal = setupQuery.data.sources.reduce((total, source) => total + (settings.sources.includes(source.id) ? source.count : 0), 0);
    return (
      <div className="page review-setup">
        <PageHeader
          title="自由练习"
          description="按自己的节奏重复词汇，不改变长期复习计划，也不会写入学习历史。"
          action={<Link className="button button--secondary button--md" to="/app/$subject/review" params={{ subject: "english" }}><RotateCcw /> 返回背诵计划</Link>}
        />
        <section className="practice-boundary" role="note"><Dumbbell /><div><strong>临时练习</strong><span>退出或刷新后，本次抽词和进度会消失。</span></div></section>
        <section className="book-selection" aria-labelledby="practice-source-title">
          <div className="book-selection__heading"><h2 id="practice-source-title">选择练习范围</h2><span>可抽取约 {selectedTotal} 个词</span></div>
          <div className="book-options">
            {setupQuery.data.sources.map((source) => {
              const selected = settings.sources.includes(source.id);
              return (
                <label key={source.id} className={selected ? "book-option book-option--selected" : "book-option"}>
                  <input type="checkbox" checked={selected} onChange={(event) => toggleSource(source.id, event.target.checked)} />
                  <span className="book-option__check">{selected ? <Check /> : null}</span>
                  <span className="book-option__copy"><strong>{source.label}</strong><small>从这一来源抽取练习词</small></span>
                  <span className="book-option__count"><strong>{source.count}</strong><small>个词</small></span>
                </label>
              );
            })}
          </div>
        </section>
        <section className="review-parameters">
          <div className="review-parameters__heading"><h2>练习设置</h2><span>只在本机记住设置</span></div>
          <div className="review-parameter-row">
            <div><strong>每轮词数</strong><small>范围 1–100</small></div>
            <div className="word-count-stepper">
              <button type="button" onClick={() => updateSettings({ count: Math.max(STUDY_TARGET_MIN, settings.count - 1) })} disabled={settings.count <= STUDY_TARGET_MIN}><Minus /></button>
              <input type="number" value={settings.count} min={STUDY_TARGET_MIN} max={STUDY_TARGET_MAX} onChange={(event) => updateSettings({ count: Math.min(STUDY_TARGET_MAX, Math.max(STUDY_TARGET_MIN, Number(event.target.value) || 1)) })} />
              <button type="button" onClick={() => updateSettings({ count: Math.min(STUDY_TARGET_MAX, settings.count + 1) })} disabled={settings.count >= STUDY_TARGET_MAX}><Plus /></button>
            </div>
          </div>
          <div className="review-parameter-row">
            <div><strong>练习轮数</strong><small>每轮使用同一组词</small></div>
            <div className="word-count-stepper">
              <button type="button" onClick={() => updateSettings({ roundCount: Math.max(PRACTICE_ROUND_MIN, settings.roundCount - 1) })} disabled={settings.roundCount <= PRACTICE_ROUND_MIN}><Minus /></button>
              <strong className="round-count-value">{settings.roundCount}</strong>
              <button type="button" onClick={() => updateSettings({ roundCount: Math.min(PRACTICE_ROUND_MAX, settings.roundCount + 1) })} disabled={settings.roundCount >= PRACTICE_ROUND_MAX}><Plus /></button>
            </div>
          </div>
          <div className="review-parameter-row">
            <div><strong>出词顺序</strong><small>随机模式每轮重新洗牌</small></div>
            <div className="review-order-control review-order-control--three">
              <button type="button" className={settings.order === "recent" ? "active" : ""} onClick={() => updateSettings({ order: "recent" })}><Clock3 /> 最近添加</button>
              <button type="button" className={settings.order === "random" ? "active" : ""} onClick={() => updateSettings({ order: "random" })}><Shuffle /> 随机</button>
              <button type="button" className={settings.order === "alphabetical" ? "active" : ""} onClick={() => updateSettings({ order: "alphabetical" })}><ArrowDownAZ /> A-Z</button>
            </div>
          </div>
          <div className="review-parameter-row">
            <div><strong>拼写训练</strong><small>想起词义后追加一次中译英</small></div>
            <label className="study-toggle"><input type="checkbox" checked={settings.spellingEnabled} onChange={(event) => updateSettings({ spellingEnabled: event.target.checked })} /><span /><strong>{settings.spellingEnabled ? "已开启" : "已关闭"}</strong></label>
          </div>
        </section>
        {error ? <p className="error-strip" role="alert">{error}</p> : null}
        <div className="review-setup__actions"><div><span>本次练习</span><strong>{Math.min(settings.count, selectedTotal)} 个词 × {settings.roundCount} 轮</strong></div><Button variant="primary" size="lg" onClick={() => void start()} disabled={starting || !settings.sources.length || !selectedTotal}><Play /> {starting ? "正在抽词…" : "开始自由练习"}</Button></div>
      </div>
    );
  }

  if (phase === "completed") {
    return (
      <main className="completion-page">
        <CelebrationConfetti />
        <section className="completion-celebration">
          <div className="completion-mark"><Check /></div>
          <p className="section-label">自由练习完成</p>
          <h1>{items.length} 个词练习了 {settings.roundCount} 轮</h1>
          <p className="completion-message">本次结果没有改变长期复习计划。</p>
          <div className="completion-metrics"><div><strong>{items.length * settings.roundCount}</strong><span>完成词次</span></div><div><strong>{attemptCount}</strong><span>作答次数</span></div><div><strong>{errorCount}</strong><span>本次纠错</span></div></div>
          <Button variant="primary" size="lg" onClick={reset} aria-keyshortcuts="Enter Space">返回练习设置 <ShortcutHint keys={["Enter", "Space"]} /></Button>
        </section>
      </main>
    );
  }

  if (!item) return <div className="page"><EmptyState title="没有抽到可练单词" description="返回后调整练习范围。" action={<Button onClick={reset}>返回练习设置</Button>} /></div>;
  const displayedSenses = item.senses;
  const progress = ((round - 1) * items.length + index) / Math.max(1, items.length * settings.roundCount) * 100;
  return (
    <div className="review-page review-page--staged">
      <header className="review-toolbar">
        <button type="button" className="study-exit" onClick={reset} aria-keyshortcuts="Escape"><RotateCcw /> 退出 <ShortcutHint keys={["Esc"]} /></button>
        <div className="review-progress"><div><span style={{ width: `${progress}%` }} /></div><strong>{index + 1} / {items.length}</strong></div>
        <div className="review-session-kind"><Dumbbell /> 自由练习 {round}/{settings.roundCount}</div>
      </header>
      <main className="review-stage review-stage--staged">
        <div className="study-stage-frame">
          {phase === "recall" ? (
            <section className="study-prompt study-prompt--recall">
              <div className="study-stage-label"><Dumbbell /> 回忆词义</div>
              <h1>{item.headword}</h1>
              <WordPronunciation wordId={item.wordId} headword={item.headword} phoneticUk={item.phoneticUk} phoneticUs={item.phoneticUs} />
              {revealed ? <div className="study-recall-answer"><WordSenses senses={displayedSenses} empty="这个词暂时没有词典释义。" /><p>对照刚才想起的含义，记录本次结果。</p></div> : <button type="button" className="study-reveal" onClick={() => setRevealed(true)} aria-keyshortcuts="Enter Space"><span>在心里回忆中文含义</span><small>准备好后显示释义 <ShortcutHint keys={["Enter", "Space"]} /></small></button>}
              {revealed ? <div className="study-stage-actions study-stage-actions--split"><Button variant="secondary" size="lg" onClick={() => finishRecall(false)} aria-keyshortcuts="1"><RotateCcw /> 没想起 <ShortcutHint keys={["1"]} /></Button><Button variant="primary" size="lg" onClick={() => finishRecall(true)} aria-keyshortcuts="2"><Check /> 想起来了 <ShortcutHint keys={["2"]} /></Button></div> : null}
            </section>
          ) : null}
          {phase === "spelling" ? (
            <section className="study-prompt study-prompt--spelling">
              <div className="study-stage-label"><Dumbbell /> 拼写巩固</div>
              <div className="study-spelling-meaning"><WordSenses senses={displayedSenses} /></div>
              <form className="study-spelling-form" onSubmit={(event) => { event.preventDefault(); if (answer.trim()) finishSpelling(); }}><input autoFocus spellCheck={false} value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="输入英文单词" /><Button type="submit" variant="primary" disabled={!answer.trim()} aria-keyshortcuts="Enter"><Check /> 确认拼写 <ShortcutHint keys={["Enter"]} /></Button></form>
            </section>
          ) : null}
          {phase === "feedback" ? (
            <section className={correct ? "study-feedback study-feedback--correct" : "study-feedback study-feedback--retry"}>
              <div className="study-feedback__mark">{correct ? <Check /> : <RotateCcw />}</div>
              <p className="section-label">{correct ? "本次通过" : "本次已记录"}</p>
              <h1>{correct ? "继续保持" : "下一轮再巩固"}</h1>
              <div className="study-feedback__word"><strong>{item.headword}</strong><WordPronunciation wordId={item.wordId} headword={item.headword} phoneticUk={item.phoneticUk} phoneticUs={item.phoneticUs} /></div>
              {!correct && answer ? <div className="study-feedback__spelling"><span>正确拼写</span><strong className="spelling-diff">{[...item.headword].map((character, characterIndex) => <span key={`${character}-${characterIndex}`} className={spellingMismatchIndexes(answer, item.headword).has(characterIndex) ? "mismatch" : ""}>{character}</span>)}</strong><small>你写的是 {answer}</small></div> : null}
              {!correct && !answer ? <div className="study-feedback__answer"><WordSenses senses={displayedSenses} /></div> : null}
              <p>自由练习不会修改复习排期。</p>
              <Button variant="primary" size="lg" onClick={next} aria-keyshortcuts="Enter Space">继续 <Play /> <ShortcutHint keys={["Enter", "Space"]} /></Button>
            </section>
          ) : null}
        </div>
      </main>
      <footer className="review-footer"><span>第 {round}/{settings.roundCount} 轮 · 当前 {index + 1}/{items.length}</span><span>尝试 {attemptCount} 次 · 纠错 {errorCount} 次</span></footer>
    </div>
  );
}
